import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Trophy, Medal, Star, Crown, ArrowLeft, RefreshCw,
  Award, Download, Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PODIUM_COLORS = {
  1: { bg: 'bg-[#face39]', shadow: 'shadow-[#d97706]', text: 'text-[#0f172a]' },
  2: { bg: 'bg-[#e2e8f0]', shadow: 'shadow-[#94a3b8]', text: 'text-[#0f172a]' },
  3: { bg: 'bg-[#fcd9b6]', shadow: 'shadow-[#d97706]', text: 'text-[#0f172a]' }
};

const ResultsPage = () => {
  const navigate = useNavigate();
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [participantId, setParticipantId] = useState(
    sessionStorage.getItem('participant_id')
  );

  useEffect(() => {
    loadLeaderboard();
  }, []);

  const loadLeaderboard = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/competition/leaderboard?top=50`);
      setLeaderboard(response.data.leaderboard);
    } catch (err) {
      console.error('Failed to load leaderboard:', err);
    } finally {
      setLoading(false);
    }
  };

  const top3 = leaderboard.slice(0, 3);
  const rest = leaderboard.slice(3, 10);
  const currentParticipant = leaderboard.find(p => p.participant_id === participantId);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#fdfdfc] flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-[#2565eb] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#2565eb] to-[#1e3a8a]">
      {/* Header */}
      <header className="p-6">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-white/70 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Home
          </button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center overflow-hidden">
              <img src="/logo.png" alt="Brain Gym" className="w-10 h-10 object-contain" />
            </div>
            <span className="text-xl font-bold text-white" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              Brain Gym
            </span>
          </div>
          <button
            onClick={loadLeaderboard}
            className="text-white/70 hover:text-white transition-colors"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
      </header>

      <main className="px-6 pb-12 max-w-4xl mx-auto">
        {/* Title */}
        <div className="text-center mb-12 animate-fade-in-up">
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            Final Results
          </h1>
          <p className="text-white/70 text-lg">Brain Gym Olympiad Champions</p>
        </div>

        {/* Podium */}
        {top3.length >= 3 && (
          <div className="flex items-end justify-center gap-4 mb-12 animate-fade-in-up stagger-1">
            {/* 2nd Place */}
            <div className="text-center">
              <div className={`w-20 h-20 sm:w-24 sm:h-24 ${PODIUM_COLORS[2].bg} rounded-full flex items-center justify-center mx-auto mb-3 shadow-[0_4px_0] ${PODIUM_COLORS[2].shadow}`}>
                <Medal className="w-10 h-10 sm:w-12 sm:h-12 text-[#64748b]" />
              </div>
              <p className="text-white font-bold mb-1">{top3[1]?.display_name}</p>
              <p className="text-white/70 text-sm">{top3[1]?.total_points} pts</p>
              <div className={`w-20 sm:w-28 h-24 ${PODIUM_COLORS[2].bg} rounded-t-2xl mx-auto mt-3 flex items-center justify-center`}>
                <span className="text-3xl font-bold text-[#64748b]">2</span>
              </div>
            </div>

            {/* 1st Place */}
            <div className="text-center -mb-8">
              <div className="relative">
                <Crown className="w-8 h-8 text-[#face39] absolute -top-10 left-1/2 -translate-x-1/2" />
                <div className={`w-24 h-24 sm:w-32 sm:h-32 ${PODIUM_COLORS[1].bg} rounded-full flex items-center justify-center mx-auto mb-3 shadow-[0_6px_0] ${PODIUM_COLORS[1].shadow}`}>
                  <Trophy className="w-12 h-12 sm:w-16 sm:h-16 text-[#0f172a]" />
                </div>
              </div>
              <p className="text-white font-bold text-lg mb-1">{top3[0]?.display_name}</p>
              <p className="text-[#face39] font-bold">{top3[0]?.total_points} pts</p>
              <div className={`w-24 sm:w-32 h-32 ${PODIUM_COLORS[1].bg} rounded-t-2xl mx-auto mt-3 flex items-center justify-center`}>
                <span className="text-4xl font-bold text-[#0f172a]">1</span>
              </div>
            </div>

            {/* 3rd Place */}
            <div className="text-center">
              <div className={`w-20 h-20 sm:w-24 sm:h-24 ${PODIUM_COLORS[3].bg} rounded-full flex items-center justify-center mx-auto mb-3 shadow-[0_4px_0] ${PODIUM_COLORS[3].shadow}`}>
                <Medal className="w-10 h-10 sm:w-12 sm:h-12 text-[#92400e]" />
              </div>
              <p className="text-white font-bold mb-1">{top3[2]?.display_name}</p>
              <p className="text-white/70 text-sm">{top3[2]?.total_points} pts</p>
              <div className={`w-20 sm:w-28 h-20 ${PODIUM_COLORS[3].bg} rounded-t-2xl mx-auto mt-3 flex items-center justify-center`}>
                <span className="text-3xl font-bold text-[#92400e]">3</span>
              </div>
            </div>
          </div>
        )}

        {/* Your Position (if not in top 3) */}
        {currentParticipant && currentParticipant.rank > 3 && (
          <div className="card-olympiad p-6 mb-8 border-2 border-[#face39] animate-fade-in-up stagger-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#2565eb] rounded-xl flex items-center justify-center text-white font-bold">
                  #{currentParticipant.rank}
                </div>
                <div>
                  <p className="font-bold text-[#0f172a]">{currentParticipant.display_name} (You)</p>
                  <p className="text-sm text-[#64748b]">{currentParticipant.accuracy}% accuracy</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-[#2565eb]">{currentParticipant.total_points}</p>
                <p className="text-xs text-[#64748b]">points</p>
              </div>
            </div>
          </div>
        )}

        {/* Top 10 Leaderboard */}
        <div className="card-olympiad p-6 animate-fade-in-up stagger-3">
          <h2 className="text-xl font-bold text-[#0f172a] mb-6" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            Top 10 Leaderboard
          </h2>

          {leaderboard.length === 0 ? (
            <p className="text-center text-[#64748b] py-8">No results yet</p>
          ) : (
            <div className="space-y-3">
              {leaderboard.slice(0, 10).map((participant, index) => {
                const isCurrentUser = participant.participant_id === participantId;
                const rank = index + 1;
                
                return (
                  <div
                    key={participant.participant_id}
                    className={`flex items-center justify-between p-4 rounded-2xl transition-all ${
                      isCurrentUser
                        ? 'bg-[#eff6ff] border-2 border-[#2565eb]'
                        : rank <= 3
                        ? 'bg-[#fef3c7]'
                        : 'bg-[#f8fafc]'
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-bold ${
                        rank === 1
                          ? 'bg-[#face39] text-[#0f172a]'
                          : rank === 2
                          ? 'bg-[#e2e8f0] text-[#64748b]'
                          : rank === 3
                          ? 'bg-[#fcd9b6] text-[#92400e]'
                          : 'bg-white text-[#64748b] border border-[#e2e8f0]'
                      }`}>
                        {rank}
                      </div>
                      <div>
                        <p className={`font-semibold ${isCurrentUser ? 'text-[#2565eb]' : 'text-[#0f172a]'}`}>
                          {participant.display_name} {isCurrentUser && '(You)'}
                        </p>
                        <p className="text-xs text-[#64748b]">
                          {participant.accuracy}% accuracy • {participant.avg_response_time}s avg
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`text-xl font-bold ${rank <= 3 ? 'text-[#d97706]' : 'text-[#0f172a]'}`}>
                        {participant.total_points}
                      </p>
                      <p className="text-xs text-[#64748b]">points</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Certificates Section */}
        <div className="card-olympiad p-6 mt-8 animate-fade-in-up stagger-4">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 bg-[#a855f7] rounded-2xl flex items-center justify-center">
              <Award className="w-8 h-8 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-[#0f172a]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                Certificates
              </h3>
              <p className="text-sm text-[#64748b]">Download your participation or achievement certificate</p>
            </div>
          </div>
          
          {participantId ? (
            <div className="flex gap-3 flex-wrap">
              <button
                onClick={() => navigate(`/certificate?participant_id=${participantId}&type=participation`)}
                className="btn-secondary flex items-center gap-2"
                data-testid="view-participation-certificate"
              >
                <Award className="w-5 h-5" />
                Participation Certificate
              </button>
              {currentParticipant && currentParticipant.rank <= 3 && (
                <button
                  onClick={() => navigate(`/certificate?participant_id=${participantId}&type=winner`)}
                  className="btn-primary flex items-center gap-2"
                  data-testid="view-winner-certificate"
                >
                  <Trophy className="w-5 h-5" />
                  Winner Certificate
                </button>
              )}
            </div>
          ) : (
            <button
              onClick={() => navigate('/certificate')}
              className="btn-secondary w-full flex items-center justify-center gap-2"
              data-testid="go-to-certificates"
            >
              <Download className="w-5 h-5" />
              View Certificates
            </button>
          )}
        </div>
      </main>
    </div>
  );
};

export default ResultsPage;
